import React, { createContext, useState, useContext, useCallback } from 'react';
import resumeService from '../services/resumeService';

const ResumeContext = createContext();

export const useResume = () => {
  const context = useContext(ResumeContext);
  if (!context) {
    throw new Error('useResume must be used within a ResumeProvider');
  }
  return context;
};

export const ResumeProvider = ({ children }) => {
  const [resumes, setResumes] = useState([]);
  const [currentResume, setCurrentResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadResumes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await resumeService.getResumes();
      setResumes(response.data.data || []);
      setError(null);
    } catch (error) {
      console.error('Error loading resumes:', error);
      setError(error.response?.data?.message || 'Error loading resumes');
      setResumes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadResume = useCallback(async (id) => {
    try {
      setLoading(true);
      const response = await resumeService.getResume(id);
      setCurrentResume(response.data.data);
      setError(null);
    } catch (error) {
      console.error('Error loading resume:', error);
      setError(error.response?.data?.message || 'Error loading resume');
      setCurrentResume(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLatestDraft = useCallback(async () => {
    try {
      setLoading(true);
      const response = await resumeService.getLatestDraft();
      setCurrentResume(response.data.data);
      setError(null);
      return response.data.data;
    } catch (error) {
      // If no draft found, backend returns 404 - treat as no draft
      if (error.response && error.response.status === 404) {
        return null;
      }
      console.error('Error loading latest draft:', error);
      setError(error.response?.data?.message || 'Error loading latest draft');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createResume = useCallback(async (resumeData) => {
    try {
      setLoading(true);
      
      // Ensure data structure matches backend expectations
      const formattedData = {
        title: resumeData.title || 'My Resume',
        contact: resumeData.contact || {},
        summary: resumeData.summary || '',
        experience: resumeData.experience || [],
        education: resumeData.education || [],
        skills: resumeData.skills || [],
        projects: resumeData.projects || [],
        certifications: resumeData.certifications || [],
        languages: resumeData.languages || [],
        activities: resumeData.activities || [],
        awards: resumeData.awards || [],
        references: resumeData.references || [],
        template: resumeData.template || 'modern',
        isPublic: resumeData.isPublic || false
      };

      const response = await resumeService.createResume(formattedData);
      const newResume = response.data.data;
      setCurrentResume(newResume);
      await loadResumes();
      setError(null);
      return newResume;
    } catch (error) {
      console.error('Error creating resume:', error);
      setError(error.response?.data?.message || 'Error creating resume');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadResumes]);

  const updateResume = useCallback(async (id, resumeData) => {
    try {
      setLoading(true);
      
      // Ensure data structure matches backend expectations
      const formattedData = {
        title: resumeData.title || 'My Resume',
        contact: resumeData.contact || {},
        summary: resumeData.summary || '',
        experience: resumeData.experience || [],
        education: resumeData.education || [],
        skills: resumeData.skills || [],
        projects: resumeData.projects || [],
        certifications: resumeData.certifications || [],
        languages: resumeData.languages || [],
        activities: resumeData.activities || [],
        awards: resumeData.awards || [],
        references: resumeData.references || [],
        template: resumeData.template || 'modern',
        isPublic: resumeData.isPublic || false,
        lastUpdated: new Date().toISOString()
      };

      const response = await resumeService.updateResume(id, formattedData);
      const updatedResume = response.data.data;
      setCurrentResume(updatedResume);
      
      // Update the resumes list
      setResumes(prev => prev.map(resume => 
        resume._id === id ? updatedResume : resume
      ));
      
      setError(null);
      return updatedResume;
    } catch (error) {
      console.error('Error updating resume:', error);
      setError(error.response?.data?.message || 'Error updating resume');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteResume = useCallback(async (id) => {
    try {
      setLoading(true);
      await resumeService.deleteResume(id);
      await loadResumes();
      if (currentResume && currentResume._id === id) {
        setCurrentResume(null);
      }
      setError(null);
    } catch (error) {
      console.error('Error deleting resume:', error);
      setError(error.response?.data?.message || 'Error deleting resume');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentResume, loadResumes]);

  const duplicateResume = useCallback(async (id) => {
    try {
      setLoading(true);
      const response = await resumeService.duplicateResume(id);
      const duplicatedResume = response.data.data;
      await loadResumes();
      setError(null);
      return duplicatedResume;
    } catch (error) {
      console.error('Error duplicating resume:', error);
      setError(error.response?.data?.message || 'Error duplicating resume');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadResumes]);

  const generateSummary = useCallback(async (id, targetRole) => {
    try {
      const response = await resumeService.generateSummary(id, targetRole);
      return response.data.data.summary;
    } catch (error) {
      console.error('Error generating summary:', error);
      setError(error.response?.data?.message || 'Error generating summary');
      throw error;
    }
  }, []);

  const analyzeResume = useCallback(async (id, jobDescription) => {
    try {
      const response = await resumeService.analyzeResume(id, jobDescription);
      return response.data.data;
    } catch (error) {
      console.error('Error analyzing resume:', error);
      setError(error.response?.data?.message || 'Error analyzing resume');
      throw error;
    }
  }, []);

  const clearCurrentResume = useCallback(() => {
    setCurrentResume(null);
  }, []);

  const resetCurrentResume = useCallback(() => {
    setCurrentResume(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = React.useMemo(() => ({
    resumes,
    currentResume,
    loading,
    error,
    loadResumes,
    loadResume,
    loadLatestDraft,
    createResume,
    updateResume,
    deleteResume,
    duplicateResume,
    generateSummary,
    analyzeResume,
    setCurrentResume,
    resetCurrentResume, // Renamed for clarity
    clearError
  }), [
    resumes,
    currentResume,
    loading,
    error,
    loadResumes,
    loadResume,
    loadLatestDraft,
    createResume,
    updateResume,
    deleteResume,
    duplicateResume,
    generateSummary,
    analyzeResume,
    resetCurrentResume,
    clearError
  ]);

  return (
    <ResumeContext.Provider value={value}>
      {children}
    </ResumeContext.Provider>
  );
};